import * as React from 'react';
import Icon, { CheckCircleFilled } from '@ant-design/icons';
import { ReactComponent as TimeCircle } from '../assets/erqi/time-circle.svg';
import { ReactComponent as CustomerService } from '../assets/erqi/customerservice.svg';
import { ReactComponent as Handler } from '../assets/erqi/handler.svg';
import { ReactComponent as ConsultIcon } from '../assets/erqi/consult.svg';
import { ReactComponent as OrderIcon } from '../assets/erqi/order.svg';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';
import { match } from 'ts-pattern';
import { useUnit } from '../hooks/useUnit';
import { packageIdLabel } from '../catalogAdapter';
import { resolveBillingMode, isUnlimitedHandleNum } from '../billingMode';
import { T, useTranslate } from '@tolgee/react';

type Color = 'blue' | 'orange' | 'green' | 'purple';
interface PackageCardProps {
  color: Color;
  title: string;
  /** 覆盖默认副标题（默认=档位/套餐名解析）；新版套餐卡片传价区文本 */
  subTitle?: React.ReactNode;
  orderedPackage: Pay.OrderedPackage;
  showHandler?: boolean;
}

export const PackageCard = React.memo((props: PackageCardProps) => {
  const { color, title, subTitle: subTitleOverride, orderedPackage, showHandler } = props;
  const { t } = useTranslate();
  const { formatter } = useUnit(1);

  const {
    id,
    validTimeStart,
    validTimeEnd,
    nonReceptionAccountNum,
    accountNum,
    status,
    handleNum,
    usedHandleNum,
    billingMode,
    trafficPackageType,
    autoRenewing,
  } = orderedPackage;

  // 企业版多计量模式：图标与文案随 billingMode 切换，空值/未知值回退处理人数
  const handlerMeta = {
    PROCESSED_BUYER: { icon: Handler, label: t('market_package_handler_count') },
    ORDER: { icon: OrderIcon, label: t('market_billing_mode_order') },
    BUYER_MESSAGE: { icon: ConsultIcon, label: t('market_billing_mode_buyer_message') },
  }[resolveBillingMode(billingMode)];

  const defaultSubTitle = (() => {
    if (id === 8)
      return trafficPackageType === 'INCREASE_THE_MONTHLY_CAP' ? (
        t('aiorder_inc_cap_tip')
      ) : (
        <>&nbsp;</>
      );
    if (id === 11) {
      return <>&nbsp;</>;
    }
    return packageIdLabel(id, t, orderedPackage.comboName, orderedPackage.regionPackageId);
  })();
  const subTitle = subTitleOverride ?? defaultSubTitle;

  const increaseQuota = match(orderedPackage)
    .with({ id: 8, trafficPackageType: 'INCREASE_THE_MONTHLY_CAP' }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('aiorder_up_quota')}:</span>
          <span>
            {t('aiorder_every_month', {
              x: formatNumber(handleNum, '0,0'),
            })}
          </span>
        </div>
      );
    })
    .with({ id: 8, trafficPackageType: 'TRAFFIC_PACKAGE' }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('aiorder_up_quota')}:</span>
          <span>{formatNumber(handleNum, '0,0')}</span>
        </div>
      );
    })
    .with({ id: 11 }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('订购额度')}:</span>
          <span>{formatNumber(handleNum, '0,0')}</span>
        </div>
      );
    })
    .otherwise(() => null);

  const handler = match(orderedPackage)
    .with({ id: 8, trafficPackageType: 'ENTERPRISE_TRAFFIC_PACKAGE' }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={handlerMeta.icon} />
          <span>
            {handlerMeta.label}:{`${formatter(usedHandleNum)}/${formatter(handleNum)}`}
          </span>
        </div>
      );
    })
    .with({ id: 8, trafficPackageType: 'INCREASE_THE_MONTHLY_CAP' }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('aiorder_month_left')}:</span>
          <span>{formatNumber(handleNum - usedHandleNum, '0,0')}</span>
        </div>
      );
    })
    .with({ id: 8, trafficPackageType: 'TRAFFIC_PACKAGE' }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('aiorder_left_traffic')}:</span>
          <span>{formatNumber(handleNum - usedHandleNum, '0,0')}</span>
        </div>
      );
    })
    .with({ id: 5 }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={handlerMeta.icon} />
          <span>
            {handlerMeta.label}:
            {isUnlimitedHandleNum(handleNum)
              ? t('market.无限')
              : `${formatter(usedHandleNum)}/${formatter(handleNum)}`}
          </span>
        </div>
      );
    })
    .with({ id: 11 }, () => {
      return (
        <div className="flex gap-1 items-center">
          <Icon component={CustomerService} />
          <span>{t('aiorder_left_traffic')}:</span>
          <span>{formatNumber(handleNum - usedHandleNum, '0,0')}</span>
        </div>
      );
    })
    .otherwise(() => null);

  return (
    <Container color={color} className="flex flex-col relative w-96 rounded-2xl">
      <Header color={color} className="rounded-t-2xl">
        <section>
          <span className="flex items-center text-sm text-content-400">
            {title}
            {autoRenewing ? <MonthTag>{t('月付')}</MonthTag> : null}
          </span>
          <span className="text-xs text-content-300">{subTitle}</span>
        </section>
        {status === 1 ? (
          <Tag color={color} className="pr-3 pl-2 h-6 rounded-tr-2xl rounded-bl-md ">
            <CheckCircleFilled />
            <span className="ml-1 text-xs">{t('market.生效中')}</span>
          </Tag>
        ) : null}
      </Header>
      <div className="flex flex-1 flex-col justify-start px-4 py-[14px]">
        {increaseQuota}
        {autoRenewing ? (
          <div className="flex flex-col">
            <div className="flex gap-1 items-center">
              <Icon component={TimeCircle} />
              <span>
                {t('自动续费')}：{t('phrase.已开启')}
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="pl-5">
                {t('下次扣费')}：{validTimeEnd}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex gap-1 items-center">
            <Icon component={TimeCircle} />
            <span>{t('market.有效期')}:</span>
            <span>{`${validTimeStart} - ${validTimeEnd}`}</span>
          </div>
        )}
        {id !== 8 && id !== 11 && (
          <div className="flex gap-1 items-center">
            <Icon component={CustomerService} />
            {nonReceptionAccountNum > 0 ? (
              <span>
                <T
                  keyName="account.不限额帐号x位限额帐号y位"
                  params={{
                    x: accountNum,
                    y: nonReceptionAccountNum,
                    num: <></>,
                  }}
                />
              </span>
            ) : (
              <span>
                {t('account.员工帐号')}: {accountNum}
              </span>
            )}
          </div>
        )}
        {showHandler && handler}
      </div>
    </Container>
  );
});

PackageCard.displayName = 'PackageCard';

const containerBgMap: Record<Color, string> = {
  blue: '#f1f5ff',
  orange: '#fff9f1',
  green: 'var(--fett-erp-card-bg)',
  purple: 'var(--fett-erp-card-bg)',
};
const Container = styled.div<{ color: Color }>`
  background: ${({ color }) => containerBgMap[color]};
  flex-basis: 384px;
  margin-bottom: 16px;
  [data-color-mode='dark'] & {
    background: var(--fett-bg-elevated);
  }
`;

const headerBgMap: Record<Color, string> = {
  blue: 'linear-gradient(to right, #b9d8f0 0%, #809cff 100%)',
  orange: 'linear-gradient(to right, #f0dab9 0%, #ffd180 100%)',
  green: 'linear-gradient(to right, #B4E9D1 0%, #58C587 100%)',
  purple: 'linear-gradient(to right, #F4DCFF 0%, #C280FF 100%)',
};
const Header = styled.div<{ color: Color }>`
  display: flex;
  height: 4rem;
  background: ${({ color }) => headerBgMap[color]};
  /* dark 下头部保留浅色渐变（用户确认），文字改回 light 的深色系保证在浅底上可读 */
  [data-color-mode='dark'] & > section > .text-content-400 {
    color: #1d2129;
  }
  [data-color-mode='dark'] & > section > .text-content-300 {
    color: #4e5969;
  }
  > section {
    flex: 1 0 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 1rem;
  }
  > div {
    flex: 0 0 auto;
    align-self: flex-start;
  }
`;

const tagBgMap: Record<Color, string> = {
  blue: '#5c96f8',
  orange: '#f8c25c',
  green: '#4BB76C',
  purple: '#B670F4',
};
const Tag = styled.div<{ color: Color }>`
  background: ${({ color }) => tagBgMap[color]};
  color: ${({ color }) => (color === 'orange' ? '#c25f1a' : '#ffffff')};
`;

const MonthTag = styled.span`
  margin-left: 6px;
  height: 18px;
  line-height: 18px;
  padding: 0 4px;
  border-radius: 2px;
  background: var(--fett-bg-info-tip);
  color: var(--fett-scenario-accent-text);
  font-size: 12px;
  font-weight: 400;
`;
